import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiChevronLeft, FiMessageCircle, FiUser, FiTrash2 } = FiIcons;

export default function PrivateRecipientPicker({
  users,
  onlineUsers,
  unreadByUser,
  recipient,
  onRecipientChange,
  onClearChat
}) {
  if (recipient) {
    const isOnline = onlineUsers.includes(recipient);
    return (
      <div className="shrink-0 border-b border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-black px-2 py-1.5 sm:px-3 sm:py-2 transition-colors">
        <div className="flex items-center justify-between gap-2 rounded-lg bg-white dark:bg-white/5 p-1.5 sm:p-2 border border-gray-200 dark:border-transparent">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onRecipientChange('')}
              aria-label="Volver a contactos"
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-950 dark:hover:text-white sm:p-1.5 transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} size={16} />
            </button>
            <div className="relative flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-red-600/10 dark:bg-red-600/20 text-red-600 dark:text-red-500">
              <SafeIcon icon={FiUser} size={12} />
              {isOnline && (
                <span className="absolute bottom-0 right-0 h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full border border-white dark:border-[#111] bg-green-500" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] sm:text-sm font-bold leading-none text-gray-900 dark:text-white">
                {recipient}
              </p>
              <p className={`mt-0.5 text-[9px] sm:text-[11px] font-medium ${isOnline ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                {isOnline ? 'En línea' : 'Sin conexión'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClearChat}
            title="Limpiar conversación"
            className="rounded-md p-1.5 text-gray-400 dark:text-gray-500 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <SafeIcon icon={FiTrash2} size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 border-b border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-black px-2 py-1.5 sm:p-3 transition-colors">
      <div className="mb-1 flex items-center gap-1 sm:gap-2">
        <SafeIcon icon={FiMessageCircle} size={10} className="text-red-600 dark:text-red-500 sm:text-sm" />
        <p className="text-[10px] sm:text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Contactos
        </p>
      </div>

      {users.length > 0 ? (
        <div className="scrollbar-hide flex gap-1.5 sm:gap-2 overflow-x-auto pb-1">
          {users.map((user) => {
            const isOnline = onlineUsers.includes(user);
            const unreadCount = unreadByUser[user] || 0;
            return (
              <button
                key={user}
                type="button"
                onClick={() => onRecipientChange(user)}
                className={`relative flex min-w-[100px] items-center gap-1.5 rounded-lg border p-1.5 sm:min-w-[118px] sm:gap-2 sm:p-2 text-left transition-all ${
                  unreadCount > 0
                    ? 'border-red-500/60 bg-red-500/10'
                    : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 hover:border-red-500 dark:hover:border-red-500/50 hover:bg-red-50 dark:hover:bg-red-500/10'
                }`}
              >
                <span className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 sm:h-8 sm:w-8">
                  <SafeIcon icon={FiUser} size={12} />
                  {isOnline && (
                    <span className="absolute bottom-0 right-0 h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full border border-white dark:border-[#111] bg-green-500" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[10px] sm:text-xs font-semibold leading-tight text-gray-900 dark:text-white">
                    {user}
                  </span>
                  <span className={`block text-[8px] sm:text-[10px] ${unreadCount > 0 ? 'font-semibold text-red-600 dark:text-red-400' : isOnline ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`} >
                    {unreadCount > 0 ? `${unreadCount} nuevo${unreadCount > 1 ? 's' : ''}` : isOnline ? 'En línea' : 'Contactar'}
                  </span>
                </span>
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 sm:h-4 sm:w-4 items-center justify-center rounded-full bg-red-600 text-[8px] font-bold text-white shadow-lg">
                    {unreadCount > 99 ? '99' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-gray-200 dark:border-white/10 p-2 text-center text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 bg-white dark:bg-transparent">
          No hay participantes activos.
        </p>
      )}
    </div>
  );
}